import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useResizeAware from 'react-resize-aware';

import { ResizableSection } from 'common/ResizableSection';
import { Stack } from 'common/ui/Layout';
import { useResizable } from 'hooks/useResizable';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { RIGHT_DRAWER_FIELD_BUILDER_MIN_HEIGHT, RightDrawer } from './RightDrawer';
import { RightDrawerWrapper } from './RightDrawerWrapper';
import {
  DEFAULT_MINIMUM_PICKLISTS_HEIGHT,
  RIGHT_DRAWER_FILTER_HEIGHT,
  RIGHT_DRAWER_WIDTH,
} from './constants';
import type { RightDrawerTabType } from './types';
import {
  FieldBuilderPaneContent,
  useIsFieldSelectionActive,
} from '../FieldBuilderPane/FieldBuilderPane';
import { resolveReporterRootBasePath } from './paths';

const DEFAULT_COLLAPSE_WIDTH = 280;
const MIN_TAB_AND_PREVIEW_HEIGHT = DEFAULT_MINIMUM_PICKLISTS_HEIGHT + RIGHT_DRAWER_FILTER_HEIGHT;

type TemplateOrMacroEditorRightDrawerProps = {
  defaultTab: RightDrawerTabType;
  procedureSmid?: string;
  loggerLabel: string;
  basePath?: string;
  collapseSize?: number;
  initialSize?: number;
  fieldBuilderMinHeight?: number;
  disabledDoubleClick?: boolean;
  isSubmittedStatus?: boolean;
  disabled?: boolean;
};

type RightDrawerFieldBuilderSectionProps = {
  fieldBuilderMinHeight: number;
};

type FullRightDrawerContentProps = {
  isFieldSelectionActive: boolean;
  fieldBuilderMinHeight: number;
  compoundDrawerHeight: number;
  procedureSmid: string;
  disabledDoubleClick: boolean;
  isSubmittedStatus: boolean;
  disabled: boolean;
};

const RightDrawerFieldBuilderSection = ({
  fieldBuilderMinHeight,
}: RightDrawerFieldBuilderSectionProps): React.ReactElement => {
  return (
    <RightDrawer.FieldBuilderSection
      minHeight={fieldBuilderMinHeight}
      flexBasis="100%"
      showDivider={false}
    >
      <FieldBuilderPaneContent />
    </RightDrawer.FieldBuilderSection>
  );
};

const FullRightDrawerContent = ({
  isFieldSelectionActive,
  fieldBuilderMinHeight,
  compoundDrawerHeight,
  procedureSmid,
  disabledDoubleClick,
  isSubmittedStatus,
  disabled,
}: FullRightDrawerContentProps): React.ReactElement => {
  const [rightDrawerResizeListener, rightDrawerSectionSizes] = useResizeAware();
  const fieldBuilderResizableProps = useResizable({
    collapseSize: fieldBuilderMinHeight,
    expandSize: fieldBuilderMinHeight,
    minimumSize: fieldBuilderMinHeight,
    dimension: 'height',
    initialSize: fieldBuilderMinHeight,
  });
  const fieldBuilderMaxHeight = useMemo(
    () => Math.max(compoundDrawerHeight - MIN_TAB_AND_PREVIEW_HEIGHT, fieldBuilderMinHeight),
    [compoundDrawerHeight, fieldBuilderMinHeight]
  );

  return (
    <>
      <RightDrawer.MacroSection />
      {isFieldSelectionActive && (
        <ResizableSection
          currentMaxHeight={fieldBuilderMaxHeight}
          minSectionHeight={fieldBuilderMinHeight}
          useResizableProps={fieldBuilderResizableProps}
          expanded={true}
          heading={null}
          topOrBottom="bottom"
        >
          <RightDrawerFieldBuilderSection fieldBuilderMinHeight={fieldBuilderMinHeight} />
        </ResizableSection>
      )}
      <Stack vertical stretchY data-stack-stretch css="min-height: 0; flex: 1;" space="none">
        {rightDrawerResizeListener}
        <RightDrawer.TabSection
          drawerHeight={
            rightDrawerSectionSizes?.height ??
            Math.max(
              compoundDrawerHeight -
                (isFieldSelectionActive ? fieldBuilderResizableProps.componentSize : 0),
              0
            )
          }
          procedureSmid={procedureSmid}
          disabledDoubleClick={disabledDoubleClick}
          isSubmittedStatus={isSubmittedStatus}
          disabled={disabled}
        />
        <RightDrawer.PreviewSection />
      </Stack>
    </>
  );
};

const FieldBuilderOnlyRightDrawerContent = ({
  fieldBuilderMinHeight,
}: RightDrawerFieldBuilderSectionProps): React.ReactElement => {
  return <RightDrawerFieldBuilderSection fieldBuilderMinHeight={fieldBuilderMinHeight} />;
};

export const TemplateOrMacroEditorRightDrawer = ({
  defaultTab,
  procedureSmid = '',
  loggerLabel,
  basePath,
  collapseSize = DEFAULT_COLLAPSE_WIDTH,
  initialSize = RIGHT_DRAWER_WIDTH,
  fieldBuilderMinHeight = RIGHT_DRAWER_FIELD_BUILDER_MIN_HEIGHT,
  disabledDoubleClick = true,
  isSubmittedStatus = false,
  disabled = false,
}: TemplateOrMacroEditorRightDrawerProps): React.ReactElement => {
  const location = useLocation();
  const isFieldSelectionActive = useIsFieldSelectionActive();
  const [isReporterNestedMacrosEnabled] = useFeatureFlagEnabled(FF.REPORTER_NESTED_MACROS);
  const [compoundDrawerHeight, setCompoundDrawerHeight] = useState(0);

  const resolvedBasePath = useMemo(() => {
    if (basePath != null) {
      return basePath;
    }

    return resolveReporterRootBasePath(location.pathname);
  }, [basePath, location.pathname]);

  return (
    <RightDrawerWrapper
      collapseSize={collapseSize}
      initialSize={initialSize}
      loggerLabel={loggerLabel}
      onHeightChange={setCompoundDrawerHeight}
    >
      <RightDrawer.Root defaultTab={defaultTab} basePath={resolvedBasePath}>
        {isReporterNestedMacrosEnabled ? (
          <FullRightDrawerContent
            isFieldSelectionActive={isFieldSelectionActive}
            fieldBuilderMinHeight={fieldBuilderMinHeight}
            compoundDrawerHeight={compoundDrawerHeight}
            procedureSmid={procedureSmid}
            disabledDoubleClick={disabledDoubleClick}
            isSubmittedStatus={isSubmittedStatus}
            disabled={disabled}
          />
        ) : (
          <FieldBuilderOnlyRightDrawerContent fieldBuilderMinHeight={fieldBuilderMinHeight} />
        )}
      </RightDrawer.Root>
    </RightDrawerWrapper>
  );
};

TemplateOrMacroEditorRightDrawer.displayName = 'TemplateOrMacroEditorRightDrawer';
